import { type ReactElement, useState } from 'react';
import type { SizeQuantities } from '@/shared/config';
import { useCustomizeEditor } from '../model/use-customize-editor';
import { CustomizeUnavailable } from './customize-unavailable';
import { EditorPanel } from './editor-panel';
import { EditorStage } from './editor-stage';

interface CustomizeEditorProps {
  readonly productId: string;
  readonly designId: string | null;
  /** 상세에서 고른 색상(`?color=`) — 새 문서를 열 때만 처음 색상 후보로 쓴다. 없으면 null. */
  readonly preferredColorId?: string | null;
  /** 상세에서 고른 사이즈별 수량(`?sizes=`) — 주문 패널의 처음 값이다. */
  readonly initialSizes?: SizeQuantities;
}

/**
 * 고객 커스터마이징 에디터 — 넓은 화면은 왼쪽 스테이지와 오른쪽 주문 패널(상품 상세 패널과 같은 결)을
 * 얇은 세로선으로 나눠 헤더 아래 화면 높이에 맞추고, 좁은 화면은 스테이지 아래로 패널을 잇는다.
 * 진입 분기는 안내 화면으로 바꾼다.
 */
export const CustomizeEditor = ({
  productId,
  designId,
  preferredColorId = null,
  initialSizes = {},
}: CustomizeEditorProps): ReactElement => {
  const editor = useCustomizeEditor({ productId, designId, preferredColorId });
  // 텍스트 내용 칸 포커스 요청 — 같은 텍스트를 다시 더블클릭해도 다시 포커스하도록 순번을 올린다.
  const [textFocus, setTextFocus] = useState<{ nodeId: string; seq: number } | null>(null);
  const requestTextFocus = (nodeId: string) =>
    setTextFocus((prev) => ({ nodeId, seq: (prev?.seq ?? 0) + 1 }));

  if (editor.status === 'loading')
    return (
      <p role="status" className="py-10 text-sm text-muted-foreground">
        {editor.message}
      </p>
    );
  if (editor.status === 'unavailable')
    return <CustomizeUnavailable kind={editor.kind} onRetry={editor.onRetry} />;

  return (
    <div className="flex flex-col gap-10 lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(380px,440px)] lg:grid-rows-[minmax(0,1fr)] lg:gap-0">
      <EditorStage editor={editor} onEditText={requestTextFocus} />
      <EditorPanel
        editor={editor}
        initialSizes={initialSizes}
        textFocusSeq={
          textFocus !== null && textFocus.nodeId === editor.store.selectedNodeId
            ? textFocus.seq
            : null
        }
      />
    </div>
  );
};
